import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { FormattedHTMLMessage } from '@bibliocommons/react-intl';
import { bindAll } from '@bibliocommons/utils-react';
import { bibShape } from '@bibliocommons/bc-prop-types';
import {
  InlineOverlay,
  InlineOverlayHeader,
  InlineOverlayFooter,
  InlineOverlayBody,
  InlineOverlayCloseButton
} from '@bibliocommons/base-inline-overlay';
import { PrimaryButton } from '@bibliocommons/deprecated-base-buttons';
import { Checkmark } from '@bibliocommons/deprecated-base-icons';
import { GenericForm } from '@bibliocommons/shared-messages';

import BibBrief from 'app/components/shared/BibBrief';
import { MATERIAL_TYPE_DIGITAL } from 'app/constants/BibConstants';
import MessagingContainer from 'app/components/Messaging/MessagingContainer';
import DigitalFormats from '../../digital-formats';
import DigitalNotificationEmail from '../../digital-notification-email';
import EnableAutoCheckouts from '../../digital-auto-checkouts';

export class DigitalHoldsWorkflowView extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      digitalNotificationEmail: this.props.digitalNotificationEmail,
      autoCheckoutsEnabled: this.props.autoCheckoutsEnabled,
      format: null
    };

    bindAll(this);
  }

  handleSubmit(e) {
    e.preventDefault();
    this.props.placeHold({
      workflowId: this.props.workflowId,
      messagingId: this.props.messagingId,
      accountId: this.props.accountId,
      metadataId: this.props.bib.get('id'),
      materialType: MATERIAL_TYPE_DIGITAL,
      enableSingleClickHolds: false,
      materialParams: {
        email: this.state.digitalNotificationEmail,
        autoCheckout: this.state.autoCheckoutsEnabled,
        format: this.state.format
      },
      reciprocalRank: this.props.reciprocalRank
    });
    this.props.handleClose();
  }

  handleEmailChange(e) {
    const digitalNotificationEmail = e.target.value;
    this.setState({
      digitalNotificationEmail
    });
  }

  handleAutoCheckoutChange(autoCheckoutsEnabled) {
    this.setState({
      autoCheckoutsEnabled
    });
  }

  getColumnClasses() {
    if (this.props.showFormatDuringHold) {
      return 'col-12 col-xs-12 col-md-6';
    }
    return 'col-12 col-xs-12';
  }

  renderQuickstartLink() {
    const { showQuickStartLink, quickStartUrl } = this.props;

    if (showQuickStartLink) {
      return <FormattedHTMLMessage id="digital_download_quickstart_link" values={{ quickStartUrl }} />;
    }

    return null;
  }

  renderFormats() {
    const { formats, provider, showFormatDuringHold, requiresFormatDuringHold, fetchDigitalFormats, bib } = this.props;
    if (showFormatDuringHold) {
      return (
        <div className={this.getColumnClasses()}>
          <DigitalFormats
            metadataId={bib.get('id')}
            formats={formats}
            provider={provider}
            requireFormats={requiresFormatDuringHold}
            fetchDigitalFormats={fetchDigitalFormats}
          />
          {this.renderQuickstartLink()}
        </div>
      );
    }

    return null;
  }

  render() {
    const { handleClose, bib, isLoading, supportAutoCheckout, messagingId } = this.props;
    return (
      <InlineOverlay handleClose={handleClose}>
        <form action="#submit-digital" onSubmit={this.handleSubmit}>
          <InlineOverlayHeader>
            <FormattedMessage id="request_this_title" />
          </InlineOverlayHeader>
          <InlineOverlayBody>
            <div className="row">
              <MessagingContainer id={messagingId}>
                {messageOptions => <GenericForm {...messageOptions} />}
              </MessagingContainer>
              <div className={this.getColumnClasses()}>
                <BibBrief bib={bib} titleTag="h3" renderTitleAsLink={false} renderAuthorsAsLink={false} />
                <EnableAutoCheckouts
                  supportAutoCheckout={supportAutoCheckout}
                  handleChange={this.handleAutoCheckoutChange}
                />
                <DigitalNotificationEmail
                  value={this.state.digitalNotificationEmail}
                  onChange={this.handleEmailChange}
                />
              </div>
              {this.renderFormats()}
            </div>
          </InlineOverlayBody>
          <InlineOverlayFooter>
            <PrimaryButton type="submit" className="pull-right" isLoading={isLoading}>
              <Checkmark color="#FFF" /> <FormattedMessage id="form_label_place_a_digital_hold" />
            </PrimaryButton>
            <InlineOverlayCloseButton className="btn btn-link pull-right">
              <FormattedMessage id="button_nevermind_close" />
            </InlineOverlayCloseButton>
          </InlineOverlayFooter>
        </form>
      </InlineOverlay>
    );
  }
}

DigitalHoldsWorkflowView.propTypes = {
  handleClose: PropTypes.func.isRequired,
  bib: bibShape.isRequired,
  isLoading: PropTypes.bool,
  formats: ImmutablePropTypes.listOf(
    ImmutablePropTypes.contains({
      name: PropTypes.string,
      hasDownloadLink: PropTypes.bool
    })
  ).isRequired,
  provider: PropTypes.string.isRequired,
  supportAutoCheckout: PropTypes.bool.isRequired,
  accountId: PropTypes.number.isRequired,
  digitalNotificationEmail: PropTypes.string,
  placeHold: PropTypes.func.isRequired,
  requiresFormatDuringHold: PropTypes.bool.isRequired,
  showFormatDuringHold: PropTypes.bool.isRequired,
  fetchDigitalFormats: PropTypes.func.isRequired,
  workflowId: PropTypes.string,
  messagingId: PropTypes.string,
  reciprocalRank: PropTypes.number,
  autoCheckoutsEnabled: PropTypes.bool,
  quickStartUrl: PropTypes.string,
  showQuickStartLink: PropTypes.bool
};

DigitalHoldsWorkflowView.displayName = 'DigitalHoldsWorkflowView';

export default DigitalHoldsWorkflowView;
